// src/lib/whatsappService.js
import pb from './pocketbaseClient';

class WhatsAppService {
  constructor() {
    this.settings = null;
    this.initialized = false;
  }

  async initialize() {
    if (this.initialized) return;
    try {
      const settings = await pb.collection('whatsapp_settings').getFirstListItem('is_active=true');
      this.settings = settings;
      this.initialized = true;
    } catch (error) {
      console.error('WhatsApp service not configured:', error);
      throw new Error('WhatsApp service not configured. Please set up API settings.');
    }
  }

  async sendTextMessage(toNumber, message, customerId = null) {
    await this.initialize();
    
    const url = `https://graph.facebook.com/v18.0/${this.settings.phone_number_id}/messages`;
    
    const payload = {
      messaging_product: 'whatsapp',
      recipient_type: 'individual',
      to: toNumber,
      type: 'text',
      text: { preview_url: false, body: message }
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.settings.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('WhatsApp API Error:', data);
      throw new Error(data.error?.message || 'Failed to send message');
    }

    // Save message to PocketBase
    await this.saveMessage({
      from_number: this.settings.phone_number_id,
      to_number: toNumber,
      message_body: message,
      direction: 'outgoing',
      status: 'sent',
      message_id: data.messages?.[0]?.id,
      customer_id: customerId
    });

    return data;
  }

  async sendTemplateMessage(toNumber, templateName, variables = [], customerId = null) {
    await this.initialize();

    const template = await pb.collection('whatsapp_templates').getFirstListItem(`name="${templateName}"`);
    if (!template) {
      throw new Error(`Template "${templateName}" not found`);
    }

    const url = `https://graph.facebook.com/v18.0/${this.settings.phone_number_id}/messages`;

    const components = [{
      type: 'body',
      parameters: variables.map((value, index) => ({
        type: 'text',
        text: value,
        parameter_name: `${index + 1}`
      }))
    }];

    const payload = {
      messaging_product: 'whatsapp',
      recipient_type: 'individual',
      to: toNumber,
      type: 'template',
      template: {
        name: templateName,
        language: { code: 'en' },
        components
      }
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.settings.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('WhatsApp Template Error:', data);
      throw new Error(data.error?.message || 'Failed to send template message');
    }

    // Save message
    await this.saveMessage({
      from_number: this.settings.phone_number_id,
      to_number: toNumber,
      message_body: `[Template: ${templateName}] ${template.body}`,
      direction: 'outgoing',
      status: 'sent',
      message_id: data.messages?.[0]?.id,
      customer_id: customerId,
      template_id: template.id
    });

    return data;
  }

  async saveMessage(data) {
    return await pb.collection('whatsapp_messages').create({
      ...data,
      is_read: true
    });
  }

  async processWebhook(payload) {
    try {
      const entries = payload.entry || [];
      for (const entry of entries) {
        const changes = entry.changes || [];
        for (const change of changes) {
          const value = change.value || {};
          const messages = value.messages || [];

          for (const message of messages) {
            // Save incoming message
            const from = message.from;
            const text = message.text?.body || '';
            
            // Check if customer exists, if not create
            let customer = await this.findOrCreateCustomer(from, message.profile?.name || 'Unknown');
            
            await this.saveMessage({
              from_number: from,
              to_number: this.settings.phone_number_id,
              message_body: text,
              direction: 'incoming',
              status: 'received',
              message_id: message.id,
              customer_id: customer?.id,
              is_read: false
            });

            // Auto-reply if enabled
            if (this.settings.auto_reply_enabled && this.settings.auto_reply_message) {
              // Check if this is the first message in the conversation
              const existing = await pb.collection('whatsapp_messages').getFullList({
                filter: `from_number="${from}" && direction="incoming"`,
                limit: 1
              });
              
              if (existing.length <= 1) {
                await this.sendTextMessage(
                  from,
                  this.settings.auto_reply_message,
                  customer?.id
                );
              }
            }
          }
        }
      }
      return { success: true };
    } catch (error) {
      console.error('Webhook processing error:', error);
      return { success: false, error: error.message };
    }
  }

  async findOrCreateCustomer(phoneNumber, name = 'Unknown') {
    try {
      // Try to find existing customer
      const customers = await pb.collection('users').getFullList({
        filter: `phone="${phoneNumber}"`
      });
      
      if (customers.length > 0) {
        return customers[0];
      }

      // Create new customer as a lead
      const newCustomer = await pb.collection('leads').create({
        name: name,
        phone: phoneNumber,
        email: `${phoneNumber}@whatsapp.customer`,
        status: 'Lead',
        source: 'WhatsApp'
      });
      
      return newCustomer;
    } catch (error) {
      console.error('Error finding/creating customer:', error);
      return null;
    }
  }

  async getConversations() {
    await this.initialize();
    const messages = await pb.collection('whatsapp_messages').getFullList({
      sort: '-created',
      expand: 'customer_id,assigned_to'
    });

    const convMap = new Map();
    for (const msg of messages) {
      const phone = msg.direction === 'incoming' ? msg.from_number : msg.to_number;
      if (!convMap.has(phone)) {
        convMap.set(phone, {
          id: phone,
          phone_number: phone,
          customer_name: msg.expand?.customer_id?.name || 'Unknown',
          customer_id: msg.expand?.customer_id?.id,
          last_message: msg.message_body,
          last_message_time: msg.created,
          unread_count: 0,
          assigned_to: msg.expand?.assigned_to,
          messages: [msg]
        });
      } else {
        const conv = convMap.get(phone);
        conv.messages.push(msg);
      }
    }

    // Calculate unread counts
    for (const conv of convMap.values()) {
      conv.unread_count = conv.messages.filter(m => !m.is_read && m.direction === 'incoming').length;
      conv.messages.sort((a, b) => new Date(a.created) - new Date(b.created));
    }

    return Array.from(convMap.values());
  }
}

export default new WhatsAppService();